import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBraceletGift } from '@/services/braceletGiftService';
import { BraceletGiftContext } from '@/contexts/braceletGiftContextValue';

export function BraceletGiftProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['bracelet-gift'],
    queryFn: fetchBraceletGift,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const [expiredAt, setExpiredAt] = useState(0);

  useEffect(() => {
    if (!data?.enabled || !data.expiresAt) return;
    const remaining = data.expiresAt - Date.now();
    if (remaining <= 0) {
      setExpiredAt(data.expiresAt);
      void queryClient.invalidateQueries({ queryKey: ['bracelet-gift'] });
      return;
    }
    const timeout = window.setTimeout(() => {
      setExpiredAt(data.expiresAt);
      void queryClient.invalidateQueries({ queryKey: ['bracelet-gift'] });
    }, Math.min(remaining + 1000, 2_147_483_647));
    return () => window.clearTimeout(timeout);
  }, [data?.enabled, data?.expiresAt, queryClient]);

  const active = useMemo(() => {
    if (!data?.enabled || (data.expiresAt && data.expiresAt <= Date.now()) || (data.expiresAt && data.expiresAt === expiredAt)) return null;
    return data;
  }, [data, expiredAt]);

  return <BraceletGiftContext.Provider value={active}>{children}</BraceletGiftContext.Provider>;
}
